/**
 * Saved views (PRD §4.11, owner decision D1): one switcher "תצוגה: ‹name› ▾"
 * per list with the list's system views plus the user's own, stored per user
 * and per list in localStorage (server storage is NL-04), and the last view the
 * user picked, which the list re-applies when it opens with a bare URL.
 * Pure helpers; the storage is injected so they are unit-tested
 * (`src/utils/__tests__/savedViewsModel.test.ts`).
 */

export interface ListView {
  id: string;
  name: string;
  /** Canonical query of the list's view keys (`listParams.canonicalQuery`). */
  query: string;
}

type ReadStore = Pick<Storage, 'getItem'>;
type WriteStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export const MAX_SAVED_VIEWS = 20;
export const MAX_VIEW_NAME = 40;

export function viewsKey(listId: string, userId: string): string {
  return `ote-views:${userId}:${listId}`;
}

export function lastViewKey(listId: string, userId: string): string {
  return `ote-views:${userId}:${listId}:last`;
}

function isView(v: unknown): v is ListView {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return typeof o.id === 'string' && typeof o.name === 'string' && typeof o.query === 'string';
}

/** The user's saved views; tolerant of missing, broken or foreign data. */
export function readViews(store: ReadStore | null | undefined, listId: string, userId: string): ListView[] {
  if (!store) return [];
  try {
    const raw = store.getItem(viewsKey(listId, userId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isView).slice(0, MAX_SAVED_VIEWS) : [];
  } catch {
    return [];
  }
}

export function writeViews(store: WriteStore | null | undefined, listId: string, userId: string, views: readonly ListView[]): boolean {
  if (!store) return false;
  try {
    if (views.length === 0) store.removeItem(viewsKey(listId, userId));
    else store.setItem(viewsKey(listId, userId), JSON.stringify(views.slice(0, MAX_SAVED_VIEWS)));
    return true;
  } catch {
    return false;
  }
}

export function cleanViewName(name: string): string {
  return name.replace(/\s+/g, ' ').trim().slice(0, MAX_VIEW_NAME);
}

/**
 * Add a view (or replace the one with the same name, case-insensitive). The
 * newest view goes last; the oldest is dropped beyond the cap.
 */
export function addView(views: readonly ListView[], name: string, query: string, makeId: () => string): ListView[] {
  const clean = cleanViewName(name);
  if (!clean) return [...views];
  const lower = clean.toLocaleLowerCase();
  const kept = views.filter((v) => v.name.toLocaleLowerCase() !== lower);
  const next = [...kept, { id: makeId(), name: clean, query }];
  return next.slice(-MAX_SAVED_VIEWS);
}

export function removeView(views: readonly ListView[], id: string): ListView[] {
  return views.filter((v) => v.id !== id);
}

/** The view whose query equals the current one: system views first, then saved. */
export function matchView<V extends ListView>(views: readonly V[], currentQuery: string): V | undefined {
  return views.find((v) => v.query === currentQuery);
}

export function readLastView(store: ReadStore | null | undefined, listId: string, userId: string): string | null {
  if (!store) return null;
  try {
    const raw = store.getItem(lastViewKey(listId, userId));
    return typeof raw === 'string' ? raw : null;
  } catch {
    return null;
  }
}

export function writeLastView(store: WriteStore | null | undefined, listId: string, userId: string, query: string): void {
  if (!store) return;
  try {
    store.setItem(lastViewKey(listId, userId), query);
  } catch {
    /* storage full or blocked: the list simply opens on its default view */
  }
}

/** localStorage when it is usable (private mode and some embeds throw). */
export function safeLocalStorage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}
